import React from "react";
import { Star } from "lucide-react";
import { RATING_META, getSkill } from "../../lib/inventorySkills";
import { FeedbackCard, EvidenceFields } from "./FeedbackCard";

const BTN = "px-2.5 py-1 text-[10px] font-bold tracking-[0.15em] border disabled:cursor-default";
const IDLE = "text-[#a0a0a0] border-white/15 hover:text-white";
const ACTIVE = { anchor: "bg-[#065f46] text-white border-[#065f46]", good: "bg-[#1e3a8a] text-white border-[#1e3a8a]", limiter: "bg-[#7f1d1d] text-white border-[#7f1d1d]" };

// One assessment item: Anchor | Good | Limiter toggle (tap active to clear → unrated), Daily/Episode for Character DNA limiters,
// feedback card (auto-open for the most recently rated item) and evidence fields.
export function ItemRow({ skill, rating, onRate, onPatch, readOnly, cardOpen, onToggleCard, showCoachNote }) {
  const value = rating?.rating || null;
  const retired = skill.status === "retired";
  const mergedName = retired && skill.mergedInto ? getSkill(skill.mergedInto)?.skill : null;
  readOnly = readOnly || retired;   // retired skills keep their old rating, read-only
  const btn = (v) => (
    <button onClick={() => !readOnly && onRate(value === v ? null : v)} disabled={readOnly}
      className={`${BTN} ${value === v ? ACTIVE[v] : IDLE}`} data-testid={`item-${skill.id}-${v}`}>{RATING_META[v].label.toUpperCase()}</button>
  );
  return (
    <div className="border border-white/5 bg-[#141414] p-3" data-testid={`item-row-${skill.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[140px] text-sm text-white font-bold flex items-center gap-2">
          {skill.skill}
          {retired && <span className="text-[8px] uppercase tracking-[0.2em] px-1.5 py-0.5 border border-[#f59e0b]/40 text-[#f59e0b]" data-testid={`item-${skill.id}-retired`} title={skill.notes || ""}>retired{mergedName ? `: now ${mergedName}` : ""}</span>}
          {!retired && skill.notes && skill.category !== "Handoffs" && <span className="text-[10px] text-[#8a8a8a] italic normal-case" data-testid={`item-${skill.id}-note`}>{skill.notes}</span>}
          {retired && skill.notes && <span className="text-[10px] text-[#8a8a8a] italic normal-case" data-testid={`item-${skill.id}-retired-note`}>{skill.notes}</span>}
          {rating?.source === "migrated" && <span className="text-[8px] uppercase tracking-[0.2em] px-1.5 py-0.5 border border-white/15 text-[#a0a0a0]" data-testid={`item-${skill.id}-migrated`}>migrated</span>}
        </div>
        <div className="flex gap-1 items-center">{btn("anchor")}{btn("good")}{btn("limiter")}
          {value && (
            <button onClick={() => !readOnly && onPatch({ isPriority: !rating?.isPrimary })} disabled={readOnly} title={rating?.isPrimary ? "Featured / Development Priority — tap to remove" : "Star it — features it in the Review and the Training tab priorities (up to 4 per lane)"}
              className={`ml-1 p-1 border ${rating?.isPrimary ? "border-[#f59e0b] bg-[#f59e0b]/10" : "border-white/10 hover:border-white/30"} disabled:cursor-default`} data-testid={`item-${skill.id}-priority`}>
              <Star className={`w-3.5 h-3.5 ${rating?.isPrimary ? "fill-[#f59e0b] text-[#f59e0b]" : "text-[#575757]"}`} />
            </button>
          )}
        </div>
      </div>
      {value === "limiter" && skill.pillar === "character" && (
        <div className="mt-2 flex items-center gap-1.5" data-testid={`item-${skill.id}-limiter-type`}>
          <span className="text-[9px] uppercase tracking-[0.3em] text-[#757575] mr-1">Type</span>
          {[["daily", "Daily"], ["episode", "Episode"]].map(([k, l]) => (
            <button key={k} disabled={readOnly} onClick={() => onPatch({ limiterType: rating.limiterType === k ? null : k })}
              className={`px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] font-bold border ${rating.limiterType === k ? "bg-[#7f1d1d] border-[#7f1d1d] text-white" : "border-white/15 text-[#a0a0a0] hover:text-white"}`}
              data-testid={`item-${skill.id}-${k}`}>{l}</button>
          ))}
          <span className="text-[9px] text-[#575757] ml-1 hidden sm:inline">Daily = not consistent day to day · Episode = a specific incident</span>
        </div>
      )}
      {value && (
        <>
          <FeedbackCard skill={skill} rating={rating} open={cardOpen} onToggle={onToggleCard} />
          <EvidenceFields skill={skill} rating={rating} onChange={onPatch} readOnly={readOnly} showCoachNote={showCoachNote} />
        </>
      )}
    </div>
  );
}
